import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Rating,
  CircularProgress,
} from "@mui/material";
import closeIcon from "src/Assets/images/CloseButton.svg";
import StarIcon from "@mui/icons-material/Star";
import { HiDownload } from "react-icons/hi";
import rating from "src/Assets/images/rating.png";
import API from "../../network";
import "./index.css";

const ReusuableTagActionModal = ({ type, tagId, tagName, tagData, label, onActionSuccess }) => {
  const [openRatingModal, setOpenRatingModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [ratingData, setRatingData] = useState({
    averageReviewOfHashtag: tagData?.averageReviewOfHashtag || 0,
    reviewsCount: tagData?.reviewsCount || 0,
    isCurrentUserGivenReview: tagData?.isCurrentUserGivenReview || "N",
    loggedInUserReview: tagData?.loggedInUserReview || 0,
  });

  useEffect(() => {
    setRatingData({
      averageReviewOfHashtag: tagData?.averageReviewOfHashtag || 0,
      reviewsCount: tagData?.reviewsCount || 0,
      isCurrentUserGivenReview: tagData?.isCurrentUserGivenReview || "N",
      loggedInUserReview: tagData?.loggedInUserReview || 0,
    });

    setSelectedRating(
      tagData?.isCurrentUserGivenReview === "Y"
        ? Number(tagData?.loggedInUserReview)
        : 0,
    );
  }, [tagData]);

  const handleDownload = async () => {
    const tenantID = tagData?.tenantID || tagData?.TenantID || tagData?.tenant_ID || tagData?.Tenant_ID;
    const tagName = tagData?.word || tagData?.hash_tag_name || tagData?.hashTag || tagData?.hashMessage;
    try {
      const response = await API.tagDownload(
        tagName,
        tenantID,
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `${tagData?.tag?.[0] || "tag"}.pdf`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading tag pdf", error);
    }
  };

  const handleClick = () => {
    if (type === "download") {
      handleDownload();
    }

    if (type === "rating") {
      setOpenRatingModal(true);
    }
  };

  const handleRating = async (starCount) => {
    try {
      // Prevent re-rating
      if (ratingData?.isCurrentUserGivenReview === "Y") {
        return;
      }

      setIsLoading(true);

      const response = await API.giveTagRating(tagId, starCount);

      // check backend success response
      if (response?.data?.status === "success") {
        setSelectedRating(starCount);

        const oldAverage = Number(ratingData?.averageReviewOfHashtag) || 0;

        const oldReviews = Number(ratingData?.reviewsCount) || 0;

        const newReviews = oldReviews + 1;

        const newAverage = (
          (oldAverage * oldReviews + starCount) /
          newReviews
        ).toFixed(1);

        setRatingData((prev) => ({
          ...prev,
          isCurrentUserGivenReview: "Y",
          loggedInUserReview: starCount,
          reviewsCount: newReviews,
          averageReviewOfHashtag: Number(newAverage),
        }));

        // close modal after rating
        setTimeout(() => {
          setOpenRatingModal(false);
          if (response?.data?.justFollowed) {
            onActionSuccess?.(() => {});
          }
        }, 200);
      }
    } catch (error) {
      console.error("Error while rating tag", error);
    } finally {
      setIsLoading(false);
    }
  };

  const averageRating = Number(ratingData?.averageReviewOfHashtag) || 0;

  const reviewsCount = Number(ratingData?.reviewsCount) || 0;

  // star color source
  const activeRating =
    ratingData?.isCurrentUserGivenReview === "Y"
      ? selectedRating // user rated
      : 0;

  const isAlreadyRated = ratingData?.isCurrentUserGivenReview === "Y";

  const getDisplayLabel = () => {
    if (label) {
      return label.replace(/\s*post$/i, "");
    }
    return type === "download" ? "Download" : "Ratings";
  };

  return (
    <>
      <div
        onClick={handleClick}
        className="tag-action-container"
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: "100%",
          cursor: "pointer",
          padding: "0.375rem 1.5rem", boxSizing: "border-box", whiteSpace: "nowrap"
        }}
      >
        {type === "download" ? (
          <>
            <HiDownload style={{ fontSize: 18, marginRight: 8 }} />
            <span style={{ fontSize: 14, whiteSpace: "nowrap"  }}>
              {getDisplayLabel()}
            </span>
          </>
        ) : (
          <>
            <img src={rating} className="tag-rating-icon" style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0, objectFit: "contain" }} alt="" />
            <span style={{ fontSize: 14, whiteSpace: "nowrap"  }}>
              {getDisplayLabel()}
            </span>
          </>
        )}
      </div>

      <Dialog
        open={openRatingModal}
        onClose={() => setOpenRatingModal(false)}
        maxWidth="xs"
        fullWidth
        sx={{
          zIndex: 4000,
        }}
        PaperProps={{
          className: "tag-rating-modal-paper",
        }}
      >
        <DialogContent className="tag-rating-modal-content">
          {/* Top Section */}
          <div className="tag-rating-header">
            <div className="tag-rating-summary">
              <div
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                {[1, 2, 3, 4, 5].map((star) => {
                  const fillPercent = Math.min(
                    Math.max((averageRating - (star - 1)) * 100, 0),
                    100,
                  );

                  return (
                    <div
                      key={star}
                      style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "6px",
                        background: "#373334",
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0.5,
                      }}
                    >
                      {/* Fill layer */}
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: `${fillPercent}%`,
                          background: "#007acc",
                          transition: "0.3s ease",
                        }}
                      />

                      {/* Star (always white) */}
                      <StarIcon
                        sx={{
                          fontSize: 16,
                          color: "#ffffff !important",
                          position: "relative",
                          zIndex: 1,
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <span className="tag-review-count">
                {reviewsCount} {reviewsCount === 1 ? "review" : "reviews"}
              </span>
            </div>

            <IconButton
              onClick={() => setOpenRatingModal(false)}
              className="tag-close-btn"
            >
              <img src={closeIcon} alt="closeIcon" />
            </IconButton>
          </div>

          <div className="tag-divider" />

          {!isAlreadyRated && (
            <div className="tag-rating-title">
              Would you like to rate this TAG?
            </div>
          )}
          {!isAlreadyRated && (
            <div className="tag-star-selection">
              {[1, 2, 3, 4, 5].map((star) => {
                const isFilled = activeRating >= star;

                return (
                  <div
                    key={star}
                    onClick={() => {
                      if (!isAlreadyRated && !isLoading) {
                        handleRating(star);
                      }
                    }}
                    style={{
                      width: "44px",
                      height: "44px",
                      borderRadius: "10px",
                      background: isFilled ? "#007acc" : "#373334",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: isAlreadyRated ? "default" : "pointer",
                      transition: "all 0.2s ease",
                      opacity: isLoading ? 0.6 : 0.5,
                    }}
                  >
                    <StarIcon
                      sx={{
                        color: "#ffffff !important",
                        fontSize: 30,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "18px",
              }}
            >
              <CircularProgress size={20} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReusuableTagActionModal;
